import { assertNever } from "@ondo/shared";
import type { Capabilities } from "@ondo/shared";

import { CollectionHead } from "../lib/CollectionHead.tsx";
import { LoadingNote, LoadingScreen } from "../lib/Loading.tsx";
import { CollectionToolbar } from "./CollectionToolbar.tsx";
import { CollectionActions, NewCollectionForm, ShareForm } from "./CollectionForms.tsx";
import { WorkCardView } from "./WorkCardView.tsx";
import { COLLECTION_COPY } from "./collection.constants.ts";
import { useCollectionController } from "./useCollectionController.ts";
import "./collection.css";

/**
 * Every work the caller can see, across every collection they hold or have been
 * shown.
 *
 * Registering is a screen of its own now, reached from the header — the wizard
 * has four steps, a fraud declaration and an authority to route to, none of
 * which belongs in an inline form beside a grid. Creating a collection is
 * reached from the header too, as a link carrying `FOCUS_PARAMS.create`; this
 * screen has no action row of its own.
 */
export function Collection({
  capabilities,
  workBase,
}: {
  capabilities: Capabilities;
  /** '/collector/me/work' — where a card's title links to. */
  workBase: string;
}) {
  const { view, owners, chooseCollection, chooseFilter, addCollection, closeCreate, share } =
    useCollectionController(capabilities, workBase);

  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={COLLECTION_COPY.loading} />;

    case "error":
      return (
        <p className="collection__error" role="alert">
          {view.message}
        </p>
      );

    case "empty":
      return (
        <section className="collection">
          <p className="collection__note">{view.message}</p>
          {view.canCreate && (
            <NewCollectionForm owners={owners} onSubmit={addCollection} busy={view.busy} />
          )}
          {/* The refusal has to land somewhere. `validateCollectionName` sets
              `actionError` for a blank name, and this branch used to render
              nothing at all — so on the one screen a new collector actually
              meets, the field emptied itself and said why to nobody. */}
          {view.error && (
            <p className="collection__error" role="alert">
              {view.error}
            </p>
          )}
        </section>
      );

    case "no-matches":
      return (
        <section className="collection">
          <CollectionToolbar
            filters={view.filters}
            activeFilter={view.activeFilter}
            onFilter={chooseFilter}
          />

          <CollectionActions
            creating={view.creating}
            owners={owners}
            onCreate={addCollection}
            onClose={closeCreate}
            busy={view.busy}
          />

          {view.error && (
            <p className="collection__error" role="alert">
              {view.error}
            </p>
          )}

          {/* The switcher sits on the works it governs, exactly as the
              dashboard panel's does. Wrapped with them rather than dropped into
              the screen's own column, so the gap under it is the head's own
              margin on both screens, and so the block keeps one height as the
              reader switches between a full collection and an empty one. */}
          <div className="collection__works">
            <CollectionHead
              name={view.activeCollectionName}
              groups={view.collectionGroups}
              selected={view.activeCollection}
              label={COLLECTION_COPY.collectionLabel}
              // Nowhere to send anyone: this screen IS every work the caller
              // can see, so there is no fuller list behind a "View all".
              link={null}
              onSelect={chooseCollection}
            />

            <p className="collection__note">{view.message}</p>
          </div>
        </section>
      );

    case "grid":
      return (
        <section className="collection">
          <CollectionToolbar
            filters={view.filters}
            activeFilter={view.activeFilter}
            onFilter={chooseFilter}
          />

          <CollectionActions
            left={
              <>
                <span>{view.summary}</span>
                {view.partial && <LoadingNote message={COLLECTION_COPY.partial} />}
              </>
            }
            creating={view.creating}
            owners={owners}
            onCreate={addCollection}
            onClose={closeCreate}
            busy={view.busy}
          />

          {view.error && (
            <p className="collection__error" role="alert">
              {view.error}
            </p>
          )}

          {/* The switcher sits on the works it governs, exactly as the
              dashboard panel's does. Wrapped with them rather than dropped into
              the screen's own column, so the gap under it is the head's own
              margin on both screens, and so the block keeps one height as the
              reader switches between a full collection and an empty one. */}
          <div className="collection__works">
            <CollectionHead
              name={view.activeCollectionName}
              groups={view.collectionGroups}
              selected={view.activeCollection}
              label={COLLECTION_COPY.collectionLabel}
              // Nowhere to send anyone: this screen IS every work the caller
              // can see, so there is no fuller list behind a "View all".
              link={null}
              onSelect={chooseCollection}
            />

            {view.cards.length === 0 ? (
              <p className="collection__note">{COLLECTION_COPY.noWorks}</p>
            ) : (
              <ul className="collection__grid">
                {view.cards.map((card) => (
                  <WorkCardView
                    key={`${card.placement.collectionId}:${card.id}`}
                    card={card}
                  />
                ))}
              </ul>
            )}
          </div>

          {view.collections.some((collection) => collection.canShare) && (
            <div className="collection__admin">
              <ShareForm collections={view.collections} onSubmit={share} busy={view.busy} />
            </div>
          )}
        </section>
      );

    default:
      return assertNever(view);
  }
}
